import { formatCurrency, MONTH_SHORT_LABELS } from '@crm/shared';
import { Button, Card, CardContent, cn, Select, Spinner } from '@crm/ui';
import { ArrowDownCircle, ArrowUpCircle, ChartNoAxesColumn, Scale } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, ExportPdfButton, PageHeader, PrintButton, StatCard } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { MANAGEMENT_ROUTES, useCashFlow } from '../api';

const SERIES = [
  { key: 'income', label: 'Entradas', color: 'bg-blue-500' },
  { key: 'expense', label: 'Saidas', color: 'bg-red-500' },
];

/** DFC: o dinheiro entrando e saindo mes a mes, no ano escolhido. */
export function CashFlowPage() {
  useDocumentTitle('DFC - fluxo de caixa');

  const navigate = useNavigate();
  const { user } = useSession();
  const units = user?.units ?? [];

  const [year, setYear] = useState(() => new Date().getFullYear());
  const [unitId, setUnitId] = useState('');

  const cashFlow = useCashFlow(year, unitId || undefined);
  const months = cashFlow.data?.months ?? [];
  const totals = cashFlow.data?.totals;

  const years = [year + 1, year, year - 1, year - 2].filter(
    (value, index, list) => list.indexOf(value) === index,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ChartNoAxesColumn />}
        title="DFC - fluxo de caixa"
        description="Entradas em azul, saidas em vermelho: o que ja aconteceu e o que esta previsto."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="DFC - fluxo de caixa" />
            <Button variant="outline" onClick={() => navigate(MANAGEMENT_ROUTES.INCOME_STATEMENT)}>
              Ver DRE
            </Button>
          </div>
        }
      />

      {/* Dois por linha no celular: empilhado, o saldo caia fora da dobra. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard
          title="Entradas no ano"
          value={formatCurrency(totals?.income ?? 0)}
          hint="Vendas no cartao e receitas do financeiro"
          icon={<ArrowUpCircle />}
        />
        <StatCard
          title="Saidas no ano"
          value={formatCurrency(totals?.expense ?? 0)}
          hint="Contas a pagar do periodo"
          icon={<ArrowDownCircle />}
        />
        <StatCard
          title="Saldo"
          value={formatCurrency(totals?.balance ?? 0)}
          hint="Entradas menos saidas"
          icon={<Scale />}
          className={cn(
            'col-span-2 sm:col-span-1',
            totals && totals.balance < 0 && 'border-destructive/40',
          )}
        />
      </div>

      <Card variant="flush">
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Select
              aria-label="Ano"
              value={String(year)}
              onChange={(event) => setYear(Number(event.target.value))}
            >
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>

            {units.length > 1 ? (
              <Select
                aria-label="Unidade"
                value={unitId}
                onChange={(event) => setUnitId(event.target.value)}
              >
                <option value="">Todas as unidades</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>

          {cashFlow.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : cashFlow.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(cashFlow.error)}</p>
          ) : (
            <BarChart
              data={months.map((month) => ({
                label: MONTH_SHORT_LABELS[month.month - 1] ?? String(month.month),
                values: { income: month.income, expense: month.expense },
              }))}
              series={SERIES}
              formatValue={formatCurrency}
              height={220}
            />
          )}
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent>
          <ul className="divide-y divide-border text-sm">
            {months.map((month) => (
              <li
                key={month.month}
                className="flex items-baseline justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
              >
                <span className="w-16 shrink-0 font-medium">
                  {MONTH_SHORT_LABELS[month.month - 1]}
                </span>
                <span className="flex flex-1 flex-wrap items-baseline justify-end gap-x-4 gap-y-1">
                  <span className="text-blue-600 dark:text-blue-400">
                    {formatCurrency(month.income)}
                  </span>
                  <span className="text-red-600 dark:text-red-400">
                    -{formatCurrency(month.expense)}
                  </span>
                  <span
                    className={
                      month.balance < 0 ? 'font-semibold text-destructive' : 'font-semibold'
                    }
                  >
                    {formatCurrency(month.balance)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
